import { useEffect, useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { SCOPE_LABELS } from '../sample.js';
import type { AnalyzingMessage } from '../types.js';

export interface AnalyzingCardProps {
  readonly message: AnalyzingMessage;
  /** Omitted when stopping is not offered (a confirmed run is already reserved). */
  readonly onStop?: ((message: AnalyzingMessage) => void) | undefined;
}

/** How long each step stays current (the whole demo wait is `ANALYZING_DELAY_MS`). */
const STEP_MS = 450;

interface Step {
  /** Label in the checklist. */
  readonly label: string;
  /** Headline while this step is the current one. */
  readonly doing: string;
}

function stepsFor(message: AnalyzingMessage): readonly Step[] {
  if (message.live) return [{ label: 'آماده‌سازی پاسخ', doing: 'در حال آماده‌سازی پاسخ…' }];
  const modules = message.modules.map((key) => SCOPE_LABELS[key]).join('، ');
  return [
    { label: 'تشخیص محدودهٔ داده و بازهٔ زمانی', doing: 'در حال تشخیص محدودهٔ داده…' },
    { label: 'بررسی دسترسی کاربر', doing: 'در حال بررسی دسترسی…' },
    {
      label: modules ? `خواندن داده‌های ${modules}` : 'خواندن داده‌ها',
      doing: modules ? `در حال خواندن داده‌های ${modules}…` : 'در حال خواندن داده‌ها…',
    },
    { label: 'در حال محاسبه…', doing: 'در حال محاسبه…' },
  ];
}

/**
 * Frame H - progress while the scripted answer is prepared: current step,
 * checklist, skeleton, and «توقف». The steps advance on a timer purely for
 * show (there is no real work behind them).
 */
export function AnalyzingCard({ message, onStop }: AnalyzingCardProps) {
  const steps = stepsFor(message);
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (current >= steps.length - 1) return undefined;
    const timer = window.setTimeout(() => setCurrent((value) => value + 1), STEP_MS);
    return () => window.clearTimeout(timer);
  }, [current, steps.length]);

  return (
    <div className="sk-card ai-state ai-analyzing" role="status" aria-live="polite">
      <div className="ai-analyzing__head">
        <span className="sk-spin" aria-hidden="true" />
        <span className="ai-analyzing__title">{steps[current]?.doing}</span>
        {onStop ? (
          <button
            type="button"
            className="sk-btn sk-btn--ghost sk-btn--sm"
            onClick={() => onStop(message)}
          >
            توقف
          </button>
        ) : null}
      </div>
      <ul className="ai-steps">
        {steps.map((step, index) => {
          const status = index < current ? 'done' : index === current ? 'active' : 'pending';
          return (
            <li key={step.label} className={`ai-steps__item ai-steps__item--${status}`}>
              {status === 'done' ? (
                <span className="ai-steps__check" aria-hidden="true">
                  <Icon name="check" size="sm" />
                </span>
              ) : status === 'active' ? (
                <span className="sk-pulse ai-steps__pulse" aria-hidden="true" />
              ) : (
                <span className="ai-steps__dot" aria-hidden="true" />
              )}
              {step.label}
            </li>
          );
        })}
      </ul>
      <div className="ai-analyzing__skeleton" aria-hidden="true">
        <div className="sk-skel ai-analyzing__line ai-analyzing__line--short" />
        <div className="sk-skel ai-analyzing__block" />
        <div className="sk-skel ai-analyzing__block" />
      </div>
    </div>
  );
}
